import { hojasDeGastoDemo } from './preview-edt';
import { PROYECTOS_DEMO } from './preview-data';

/**
 * Datos sembrados del equipo de un proyecto, para la vista previa.
 *
 * Los casos son los que la pantalla tiene que saber mostrar y que no aparecen
 * mirando un equipo prolijo: una asignación que Trakkinn todavía no recibió,
 * una que rechazó, una dada de baja con horas ya cargadas, alguien sin tasa,
 * gente imputando sin estar asignada, y horas sin elemento de EDT.
 */

/* **Los elementos salen de la EDT sembrada, no de una lista propia.** Antes
   eran tres inventados acá, y en la vista previa la planificación ofrecía
   líneas que no existían en la pestaña EDT del mismo proyecto: dos verdades
   distintas para el mismo dato. */
const ELEMENTOS = hojasDeGastoDemo().map((h) => ({
  ...h,
  presupuesto: null,
  bloqueado: false
}));

/* Los integrantes apuntan a **los mismos elementos** que ofrece el selector.
   Antes tenían ids fijos (92, 93, 94) que dejaron de existir cuando la lista
   pasó a salir de la EDT sembrada: las filas se veían sin elemento y parecía
   un error de la pantalla. */
const ELEM = (i: number) => ELEMENTOS[i % ELEMENTOS.length] ?? ELEMENTOS[0];

const INTEGRANTES = [
  {
    id: 1187,
    employee_id: 39,
    nombre: 'Eliana Gabriela Alvarez',
    email: 'ealvarez@vippinn.com',
    rol: 'Consultor Senior',
    codigo_tasa: 'CON33BS',
    unidad_negocio: 'Consultoría',
    edt_item_id: ELEM(0).id,
    edt_elemento: ELEM(0).descripcion,
    edt_numero: ELEM(0).numero,
    desde: '2026-09-01',
    hasta: '2026-12-31',
    ritmo: 'dia' as const,
    horas_por_ritmo: '6.00',
    tramos: [
      { desde: '2026-09-01', hasta: '2026-09-01', horas: 6 },
      { desde: '2026-09-02', hasta: '2026-09-02', horas: 6 },
      { desde: '2026-09-03', hasta: '2026-09-03', horas: 6 },
      { desde: '2026-09-04', hasta: '2026-09-04', horas: 6 },
      { desde: '2026-09-07', hasta: '2026-09-07', horas: 6 },
      { desde: '2026-09-08', hasta: '2026-09-08', horas: 6 },
      { desde: '2026-09-09', hasta: '2026-09-09', horas: 6 },
      { desde: '2026-09-10', hasta: '2026-09-10', horas: 6 },
      { desde: '2026-09-11', hasta: '2026-09-11', horas: 6 }
    ],
    tope_mensual: 120,
    jornada_mensual: 160,
    tasa: '21180.03',
    tasa_moneda: 'ARS',
    costo_planificado: 10166414.4,
    horas_imputadas_aprobadas: 96,
    horas_imputadas_sin_aprobar: 48,
    horas_fuera_de_plan: 16,
    costo_fuera_de_plan: 338880,
    costo_fuera_de_plan_moneda: 'ARS',
    horas_imputadas_rechazadas: 8,
    costo_imputado_aprobado: 2033283,
    costo_imputado_sin_aprobar: 1016641,
    costo_imputado_moneda: 'ARS',
    horas_sin_costo: 0,
    habilitada: true,
    puede_imputar: true,
    avisado_a_trakkinn: '2026-09-07T14:22:10-03:00',
    es_borrador: false,
    rechazo_de_trakkinn: null,
    nota: null
  },
  {
    /* Planificada y sin avisar: existe acá y la persona no puede imputar. */
    id: 1188,
    employee_id: 44,
    nombre: 'Martín Ferreyra',
    email: 'mferreyra@vippinn.com',
    rol: 'QA Senior',
    codigo_tasa: 'CON21BS',
    unidad_negocio: 'Consultoría',
    edt_item_id: ELEM(1).id,
    edt_elemento: ELEM(1).descripcion,
    edt_numero: ELEM(1).numero,
    desde: '2026-09-15',
    hasta: '2026-11-30',
    ritmo: 'semana' as const,
    horas_por_ritmo: '20.00',
    tramos: [
      { desde: '2026-09-15', hasta: '2026-09-20', horas: 20 },
      { desde: '2026-09-21', hasta: '2026-09-27', horas: 20 }
    ],
    tope_mensual: 88,
    jornada_mensual: 160,
    tasa: '18450.00',
    tasa_moneda: 'ARS',
    costo_planificado: 4059000,
    horas_imputadas_aprobadas: 0,
    horas_imputadas_sin_aprobar: 0,
    horas_fuera_de_plan: 0,
    costo_fuera_de_plan: null,
    costo_fuera_de_plan_moneda: null,
    horas_imputadas_rechazadas: 0,
    costo_imputado_aprobado: null,
    costo_imputado_sin_aprobar: null,
    costo_imputado_moneda: null,
    horas_sin_costo: 0,
    habilitada: true,
    puede_imputar: false,
    avisado_a_trakkinn: null,
    es_borrador: false,
    rechazo_de_trakkinn: null,
    nota: null
  },
  {
    /* Rechazada por Trakkinn: el motivo dice qué hacer. */
    id: 1189,
    employee_id: 51,
    nombre: 'Carolina Ruiz',
    email: 'cruiz@vippinn.com',
    rol: 'Analista',
    codigo_tasa: 'ANL11ES',
    unidad_negocio: 'Estructura',
    edt_item_id: ELEM(2).id,
    edt_elemento: ELEM(2).descripcion,
    edt_numero: ELEM(2).numero,
    desde: '2026-09-01',
    hasta: '2026-10-31',
    ritmo: 'dia' as const,
    horas_por_ritmo: '4.00',
    tramos: [
      { desde: '2026-09-14', hasta: '2026-09-14', horas: 4 },
      { desde: '2026-09-15', hasta: '2026-09-15', horas: 4 },
      { desde: '2026-09-16', hasta: '2026-09-16', horas: 4 }
    ],
    tope_mensual: 80,
    jornada_mensual: 80,
    /* Familia ES: no tiene tasa en ningún tarifario. Se planifica igual. */
    tasa: null,
    tasa_moneda: null,
    costo_planificado: null,
    horas_imputadas_aprobadas: 0,
    horas_imputadas_sin_aprobar: 0,
    horas_fuera_de_plan: 0,
    costo_fuera_de_plan: null,
    costo_fuera_de_plan_moneda: null,
    horas_imputadas_rechazadas: 0,
    costo_imputado_aprobado: null,
    costo_imputado_sin_aprobar: null,
    costo_imputado_moneda: null,
    horas_sin_costo: 0,
    habilitada: true,
    puede_imputar: false,
    avisado_a_trakkinn: null,
    /* Nunca estuvo vigente: se puede borrar de verdad. */
    es_borrador: true,
    rechazo_de_trakkinn:
      'El correo coincide con 2 usuarios de Trackinn (ids: 9, 244) y no se pudo desempatar.',
    nota: null
  },
  {
    /* De baja, con horas ya cargadas: siguen contando. */
    id: 1190,
    employee_id: 47,
    nombre: 'Diego Sosa',
    email: 'dsosa@vippinn.com',
    rol: 'Desarrollador',
    codigo_tasa: 'DES11PF',
    unidad_negocio: 'Tecnología',
    edt_item_id: ELEM(0).id,
    edt_elemento: ELEM(0).descripcion,
    edt_numero: ELEM(0).numero,
    desde: '2026-07-01',
    hasta: '2026-08-31',
    ritmo: 'dia' as const,
    horas_por_ritmo: '8.00',
    tramos: [
      { desde: '2026-07-01', hasta: '2026-07-01', horas: 8 },
      { desde: '2026-07-02', hasta: '2026-07-02', horas: 8 },
      { desde: '2026-07-03', hasta: '2026-07-03', horas: 8 },
      { desde: '2026-08-03', hasta: '2026-08-03', horas: 8 },
      { desde: '2026-08-04', hasta: '2026-08-04', horas: 8 }
    ],
    /* Horas anteriores a que Trakkinn congelara la tasa en cada imputación:
       existen, están medidas y no se pueden valuar. */
    sin_tasa_en_imputaciones: true,
    tope_mensual: 160,
    jornada_mensual: 160,
    tasa: '31000.00',
    tasa_moneda: 'ARS',
    costo_planificado: 9920000,
    horas_imputadas_aprobadas: 288,
    horas_imputadas_sin_aprobar: 0,
    horas_fuera_de_plan: 0,
    costo_fuera_de_plan: null,
    costo_fuera_de_plan_moneda: null,
    horas_imputadas_rechazadas: 0,
    /* Horas viejas sin tasa congelada: no se pueden valuar, y por eso el costo
       imputado va en null y las horas se informan aparte. */
    costo_imputado_aprobado: null,
    costo_imputado_sin_aprobar: null,
    costo_imputado_moneda: null,
    horas_sin_costo: 288,
    habilitada: false,
    puede_imputar: false,
    avisado_a_trakkinn: '2026-07-02T10:00:00-03:00',
    es_borrador: false,
    rechazo_de_trakkinn: null,
    nota: 'Pasó al proyecto de Andina.'
  }
];

const COLADOS = [
  {
    employee_id: 62,
    nombre: 'Lucía Benítez',
    email: 'lbenitez@vippinn.com',
    horas: 64,
    horas_imputadas_aprobadas: 48,
    horas_imputadas_sin_aprobar: 16,
    horas_imputadas_rechazadas: 0,
    costo_imputado_aprobado: 1016640,
    costo_imputado_sin_aprobar: 338880,
    costo_imputado_moneda: 'ARS',
    horas_sin_costo: 0,
    tasa: '21180.03',
    tasa_moneda: 'ARS',
    imputaciones: 8,
    primera: '2026-08-04',
    ultima: '2026-09-05',
    se_puede_asignar: true
  },
  {
    /* Existe en Trakkinn y no en ARMUS: no se puede asignar desde acá. */
    employee_id: null,
    nombre: 'Pablo Iriarte',
    email: 'piriarte@vippinn.com',
    horas: 12,
    horas_imputadas_aprobadas: 12,
    horas_imputadas_sin_aprobar: 0,
    horas_imputadas_rechazadas: 0,
    costo_imputado_aprobado: null,
    costo_imputado_sin_aprobar: null,
    costo_imputado_moneda: null,
    horas_sin_costo: 12,
    tasa: null,
    tasa_moneda: null,
    imputaciones: 2,
    primera: '2026-09-01',
    ultima: '2026-09-03',
    se_puede_asignar: false
  }
];

const CANDIDATOS = [
  {
    id: 39, nombre: 'Eliana Gabriela Alvarez', email: 'ealvarez@vippinn.com',
    codigo_rol: 'CON33BS', rol: 'Consultor Senior', unidad_negocio: 'Consultoría',
    tasa: '21180.03', tasa_moneda: 'ARS', tasa_utilizable: true, jornada_semanal: '40.00', jornada_mensual: 160
  },
  {
    id: 55, nombre: 'Nicolás Paz', email: 'npaz@vippinn.com',
    codigo_rol: 'CON33BS', rol: 'Consultor Senior', unidad_negocio: 'Consultoría',
    /* El mismo código de rol que la anterior, en dólares. */
    tasa: '47.73', tasa_moneda: 'USD', tasa_utilizable: true, jornada_semanal: '40.00', jornada_mensual: 160
  },
  {
    id: 51, nombre: 'Carolina Ruiz', email: 'cruiz@vippinn.com',
    codigo_rol: 'ANL11ES', rol: 'Analista', unidad_negocio: 'Estructura',
    tasa: null, tasa_moneda: null, tasa_utilizable: false, jornada_semanal: '20.00', jornada_mensual: 80
  }
];

/* **La vista previa guarda en memoria.** Antes contestaba «ok» y no guardaba
   nada, así que después de grabar volvía lo sembrado y no había forma de probar
   el circuito: guardar, ver el toast y comprobar que la tarjeta se actualiza.
   Vive mientras dura la pestaña; al recargar el navegador vuelve a lo sembrado. */
/* Todo sale sembrado: los totales de cada fila se calculan de sus tramos y de
   las imputaciones que de ahí se derivan, así la tabla y el modal no se pueden
   contradecir. */
let equipo: IntegranteDemo[] = (INTEGRANTES as IntegranteDemo[]).map(sembrar);
let proximoId = 2000;

function guardarEnMemoria(cuerpo: any): unknown {
  const tramos = (cuerpo?.tramos ?? []).filter((t: any) => Number(t?.horas) > 0);
  const horas = tramos.reduce((s: number, t: any) => s + Number(t.horas), 0);

  const elemento = ELEMENTOS.find((e) => e.id === cuerpo?.edt_item_id);
  const persona = CANDIDATOS.find((c) => c.id === cuerpo?.employee_id);

  /* Se busca por persona y elemento, que es lo que identifica una asignación:
     guardar dos veces la misma fila la actualiza en vez de duplicarla. */
  const i = equipo.findIndex(
    (x: any) => x.employee_id === cuerpo?.employee_id && x.edt_item_id === cuerpo?.edt_item_id
  );

  const fila: any = {
    ...(i >= 0 ? equipo[i] : {}),
    id: i >= 0 ? (equipo[i] as any).id : proximoId++,
    employee_id: cuerpo?.employee_id,
    nombre: persona?.nombre ?? (i >= 0 ? (equipo[i] as any).nombre : 'Sin nombre'),
    email: persona?.email ?? null,
    rol: persona?.rol ?? null,
    codigo_tasa: persona?.codigo_rol ?? null,
    unidad_negocio: persona?.unidad_negocio ?? null,
    edt_item_id: cuerpo?.edt_item_id,
    edt_elemento: elemento?.descripcion ?? null,
    ritmo: cuerpo?.ritmo ?? 'dia',
    horas_por_ritmo: null,
    /* La ventana y el total salen del detalle, igual que en el backend. */
    desde: tramos.length ? tramos[0].desde : null,
    hasta: tramos.length ? tramos[tramos.length - 1].hasta : null,
    horas_planificadas: horas,
    tope_mensual: null,
    jornada_mensual: 160,
    tasa: persona?.tasa ?? null,
    tasa_moneda: persona?.tasa_moneda ?? null,
    costo_planificado: persona?.tasa ? horas * Number(persona.tasa) : null,
    horas_imputadas_aprobadas: 0,
    horas_imputadas_sin_aprobar: 0,
    horas_fuera_de_plan: 0,
    habilitada: true,
    puede_imputar: false,
    avisado_a_trakkinn: null,
    /* Recien creada acá: nunca estuvo vigente. */
    es_borrador: i >= 0 ? (equipo[i] as any).es_borrador ?? true : true,
    rechazo_de_trakkinn: null,
    nota: null,
    tramos
  };

  if (i >= 0) {
    equipo[i] = fila;
  } else {
    equipo = [...equipo, fila];
  }

  return { success: true, message: 'Asignación guardada.', integrante: fila };
}

export function respuestaEquipo(
  metodo: string,
  ruta: string,
  params: URLSearchParams,
  cuerpo?: unknown
): unknown | null {
  /* Lo ocupado en otros proyectos, mes a mes (3/11/2026). Cada persona con un
     caso: una tomada a medias que se libera en enero, una sin otros proyectos
     y una sin jornada informada. */
  if (metodo === 'GET' && /\/api\/equipo\/ocupacion$/.test(ruta)) {
    const hoy = new Date();
    const ids = (params.get('personas') ?? '').split(',').map(Number).filter(Boolean);
    const otros: Record<number, number[]> = { 39: [90, 100, 40], 44: [], 51: [] };

    return {
      success: true,
      personas: ids.map((id) => ({
        employee_id: id,
        jornada_mensual: id === 51 ? 80 : 160,
        sin_tope: false,
        meses: Array.from({ length: 6 }, (_, i) => {
          const d = new Date(hoy.getFullYear(), hoy.getMonth() + i, 1);
          const dias = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
          const desde = i === 0 ? hoy.getDate() : 1;
          const capacidad = Math.round((id === 51 ? 80 : 160) * (dias - desde + 1) / dias * 10) / 10;
          const ocupado = otros[id]?.[i] ?? 0;

          return {
            mes: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            capacidad,
            ocupado,
            libre: Math.max(0, capacidad - ocupado),
            por_proyecto: ocupado ? [{ codigo: 'SFOIC1', proyecto: 'Implementación Odoo', horas: ocupado }] : []
          };
        })
      }))
    };
  }

  /* Una fila del equipo, abierta. Las imputaciones por día se derivan de los
     totales del mismo integrante en vez de escribirse aparte: si fueran datos
     sueltos, el detalle sumaría distinto que la fila de la que salió. */
  const detalle = ruta.match(/\/api\/project\/[^/]+\/equipo\/(\d+)\/detalle$/);

  if (metodo === 'GET' && detalle) {
    const id = Number(detalle[1]);
    const i = equipo.find((p) => p.id === id);

    if (!i) return { success: false, message: 'Esa asignación no es de este proyecto.' };

    return {
      success: true,
      integrante: enLaMoneda(i, params.get('moneda')),
      granularidad: i.ritmo ?? 'semana',
      tramos: (i.tramos ?? []).map((tr) => ({ ...tr, granularidad: i.ritmo ?? 'semana' })),
      imputaciones_por_dia: imputacionesDemo(i)
    };
  }

  if (/\/api\/project\/[^/]+\/equipo\/elementos$/.test(ruta)) {
    return { success: true, elementos: ELEMENTOS, excluidos: { con_hijos: 2, de_ingreso: 1 } };
  }

  if (metodo === 'GET' && /\/api\/project\/[^/]+\/equipo$/.test(ruta)) {
    const monedaVista = params.get('moneda');
    /* El estado sale del proyecto sembrado y no de una constante: el equipo
       sólo se modifica en Planificación y Replanificación, y con un valor fijo
       la pantalla mostraría el botón en proyectos donde no corresponde. */
    const codigo = decodeURIComponent(ruta.split('/project/')[1].split('/')[0]);
    const proyecto = PROYECTOS_DEMO.find((p) => p.code === codigo);
    const estado = proyecto?.estado ?? null;

    return {
      success: true,
      proyecto: {
        codigo,
        nombre: proyecto?.name ?? null,
        estado,
        se_puede_planificar: (estado ?? '').toLowerCase().includes('planificaci')
      },
      integrantes: equipo.map((i) => enLaMoneda(i, monedaVista)),
      imputan_sin_estar_asignados: COLADOS,
      horas_sin_elemento: { horas: 1240, imputaciones: 155, ultima: '2026-09-05' },

      /* La serie sale de los mismos tramos e imputaciones sembrados. Los meses
         viejos vienen **sin costo imputado**: sus horas son anteriores a que
         Trakkinn congelara la tasa, así que no se pueden valuar. */
      serie_mensual: serieMensualDemo(),
      // Lo ejecutado de todos, con alguien que ya no está asignado (2/11/2026)
      ejecutado_historico: [
        { employee_id: 1, nombre: 'Ana Pérez', aprobadas: 590, sin_aprobar: 12, fuera_de_plan: 8, meses: [
          { mes: '2026-04', aprobadas: 128, sin_aprobar: 0, fuera_de_plan: 0 }, { mes: '2026-05', aprobadas: 152, sin_aprobar: 0, fuera_de_plan: 0 },
          { mes: '2026-06', aprobadas: 77, sin_aprobar: 0, fuera_de_plan: 0 }, { mes: '2026-07', aprobadas: 136, sin_aprobar: 0, fuera_de_plan: 0 },
          { mes: '2026-08', aprobadas: 77, sin_aprobar: 0, fuera_de_plan: 0 }, { mes: '2026-09', aprobadas: 20, sin_aprobar: 12, fuera_de_plan: 8 }] },
        { employee_id: 99, nombre: 'Ya no asignado', aprobadas: 61, sin_aprobar: 0, fuera_de_plan: 2, meses: [
          { mes: '2026-04', aprobadas: 40, sin_aprobar: 0, fuera_de_plan: 0 }, { mes: '2026-05', aprobadas: 21, sin_aprobar: 0, fuera_de_plan: 2 }] }
      ]
    };
  }

  if (ruta.endsWith('/api/equipo/candidatos')) {
    const buscar = (params.get('buscar') ?? '').toLowerCase();

    /* La tasa en la moneda pedida, como el backend: con las cotizaciones de
       muestra (cuánto vale un dólar en cada una). */
    const moneda = (params.get('moneda') ?? '').toUpperCase();
    const porDolar: Record<string, number> = { USD: 1, ARS: 1410, EUR: 0.92 };
    type Persona = { tasa: string | null; tasa_moneda: string | null; tasa_utilizable: boolean } & Record<string, unknown>;
    const convertir = (c: Persona) => {
      const desde = porDolar[(c.tasa_moneda ?? '').toUpperCase()];
      const hacia = porDolar[moneda];
      const tasa = Number(c.tasa);
      return {
        ...c,
        tasa_vista: c.tasa_utilizable && desde && hacia ? Math.round((tasa / desde) * hacia * 100) / 100 : null,
        moneda_vista: moneda || null
      };
    };
    const lista = buscar
      ? CANDIDATOS.filter((c) => `${c.nombre} ${c.email} ${c.codigo_rol}`.toLowerCase().includes(buscar))
      : CANDIDATOS;

    /* Las que ya están en la grilla vienen siempre, como en el backend, y
       marcadas: el selector no las ofrece. */
    const incluir = params.getAll('incluir[]').map(Number);
    const deLaGrilla = equipo
      .filter((i) => incluir.includes(i.employee_id) && !lista.some((c) => c.id === i.employee_id))
      .map((i) => ({
        ...CANDIDATOS[0],
        id: i.employee_id,
        nombre: i.nombre ?? '—',
        email: i.email ?? null,
        codigo_rol: i.codigo_tasa ?? null,
        rol: i.rol ?? null,
        unidad_negocio: i.unidad_negocio ?? null,
        tasa: i.tasa ?? null,
        tasa_moneda: i.tasa_moneda ?? null,
        tasa_utilizable: i.tasa != null && i.tasa_moneda != null,
        solo_planificado: true
      }));

    return { success: true, personas: ([...lista, ...deLaGrilla] as Persona[]).map(convertir) };
  }

  if (ruta.endsWith('/api/equipo/carga')) {
    const horas = Number(params.get('horas') ?? 0);

    return {
      success: true,
      carga: {
        horas: 140 + horas,
        horas_nuevas: horas,
        capacidad_semanal: 40,
        capacidad_en_ventana: 171.4,
        porcentaje: Math.round(((140 + horas) / 171.4) * 1000) / 10,
        excedida: 140 + horas > 171.4,
        motivo_sin_tope: null,
        por_proyecto: [{ proyecto: 'Servicio AM LABCO', codigo: 'SFLA01', horas: 140 }]
      },
      restriccion: {
        aplica: true,
        horas_ya_imputadas: 64,
        ultima: '2026-09-05',
        aviso: 'Ya imputa en este proyecto sin estar planificada acá.'
      }
    };
  }

  /* Guardar en la vista previa: contesta como el backend para poder probar el
     circuito completo sin base. No persiste: al recargar vuelve lo sembrado. */
  if (metodo === 'POST' && /\/api\/project\/[^/]+\/equipo$/.test(ruta)) {
    return guardarEnMemoria(cuerpo);
  }

  if (metodo === 'PUT' && /\/api\/project\/[^/]+\/equipo\/\d+$/.test(ruta)) {
    return guardarEnMemoria(cuerpo);
  }

  /* Sacar del equipo: **borra si es borrador, deshabilita si ya estuvo
     vigente**. Sin esto la llamada salía a la red y la pantalla decía que no se
     pudo sacar, cuando lo único que faltaba era que alguien contestara. */
  const baja = ruta.match(/\/api\/project\/[^/]+\/equipo\/(\d+)$/);

  if (metodo === 'DELETE' && baja) {
    const id = Number(baja[1]);
    const fila: any = equipo.find((x: any) => x.id === id);

    if (!fila) {
      return { success: false, message: 'No existe esa asignación.' };
    }

    if (fila.es_borrador) {
      equipo = equipo.filter((x: any) => x.id !== id);

      return {
        success: true,
        message: 'Fila eliminada. Nunca llegó a estar vigente.',
        integrante: null
      };
    }

    fila.habilitada = false;
    fila.puede_imputar = false;
    fila.avisado_a_trakkinn = null;

    return { success: true, message: 'Asignación dada de baja.', integrante: fila };
  }

  if (metodo === 'POST' && ruta.endsWith('/equipo/avisar')) {
    return {
      success: true,
      message: '2 avisadas y 1 rechazada por Trakkinn.',
      enviadas: 4, aceptadas: 2, tarde: false,
      rechazadas: [{ referencia: 1189, motivo: 'El correo coincide con 2 usuarios de Trackinn.' }]
    };
  }

  return null;
}


/**
 * Las horas imputadas día a día, **derivadas del plan**.
 *
 * Antes se repartían los totales sueltos del integrante entre los días hábiles
 * de su ventana, y salían números que se contradecían entre sí: un día con 6
 * horas planificadas mostraba 19 aprobadas, 9 sin aprobar y sólo 3 fuera de
 * plan. Con esos datos no se puede juzgar si la pantalla calcula bien, porque
 * lo que está mal es lo que entra.
 *
 * Ahora salen del plan: cada día imputa lo que tenía planificado, y **lo que
 * excede el plan es exactamente lo que queda marcado fuera de plan**. Es la
 * misma relación que hay en producción, donde la marca la pone Trakkinn al
 * dejar cargar por encima del tope.
 */
function imputacionesDemo(i: IntegranteDemo): Array<Record<string, unknown>> {
  const hoy = new Date();
  const limite = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
    + `-${String(hoy.getDate()).padStart(2, '0')}`;

  /* Lo planificado por día. Un tramo de una semana se reparte entre sus días
     hábiles: acá es dato inventado y se puede, en la pantalla no —ahí sería
     afirmar un reparto que nadie planificó—. */
  const plan = new Map<string, number>();

  for (const tr of i.tramos ?? []) {
    const dias = habilesEntre(tr.desde, tr.hasta);

    if (dias.length === 0) continue;

    for (const d of dias) {
      plan.set(d, (plan.get(d) ?? 0) + tr.horas / dias.length);
    }
  }

  const tasa = i.tasa ? Number(i.tasa) : null;
  const valuable = tasa !== null && tasa > 0 && !!i.tasa_moneda && !i.sin_tasa_en_imputaciones;

  const filas: Array<Record<string, unknown>> = [];
  let n = 0;

  for (const [fecha, planificadas] of [...plan.entries()].sort()) {
    /* El futuro no se imputó todavía: es planificación, no trabajo hecho. */
    if (fecha > limite) continue;

    n++;

    /* Cada tercer día se carga de más. Es el caso que la pantalla existe para
       mostrar: Trakkinn avisa y deja cargar igual. */
    const imputadas = redondear(n % 3 === 0 ? planificadas * 1.5 : planificadas);
    /* Un día de cada cuatro queda entero pendiente de aprobar. */
    const aprobadas = n % 4 === 0 ? 0 : imputadas;
    const sinAprobar = redondear(imputadas - aprobadas);
    const fueraDePlan = Math.max(0, redondear(imputadas - planificadas));
    const rechazadas = n % 5 === 0 ? 2 : 0;

    filas.push({
      fecha,
      aprobadas,
      sin_aprobar: sinAprobar,
      rechazadas,
      fuera_de_plan: fueraDePlan,
      /* El desvío partido: lo cargado de más y ya aprobado es costo
         confirmado; lo cargado de más y sin aprobar todavía se puede caer. */
      fuera_de_plan_aprobadas: aprobadas > 0 ? fueraDePlan : 0,
      fuera_de_plan_sin_aprobar: aprobadas > 0 ? 0 : fueraDePlan,
      costo_aprobado: valuable ? Math.round(aprobadas * (tasa as number)) : null,
      costo_sin_aprobar: valuable ? Math.round(sinAprobar * (tasa as number)) : null,
      costo_fuera_de_plan: valuable ? Math.round(fueraDePlan * (tasa as number)) : null,
      costo_fuera_de_plan_aprobado:
        valuable ? Math.round((aprobadas > 0 ? fueraDePlan : 0) * (tasa as number)) : null,
      costo_fuera_de_plan_sin_aprobar:
        valuable ? Math.round((aprobadas > 0 ? 0 : fueraDePlan) * (tasa as number)) : null,
      costo_moneda: valuable ? i.tasa_moneda ?? null : null,
      /* Sin tasa congelada no se pueden valuar, y se dicen: si no, el costo de
         al lado se lee como si fuera el de todas las horas. */
      horas_sin_costo: valuable ? 0 : imputadas
    });
  }

  return filas;
}

/** Los días hábiles de un rango, que son los que se planifican */
function habilesEntre(desde: string, hasta: string): string[] {
  const dias: string[] = [];
  const fin = new Date(`${hasta}T00:00:00`);

  for (const d = new Date(`${desde}T00:00:00`); d <= fin; d.setDate(d.getDate() + 1)) {
    if (d.getDay() === 0 || d.getDay() === 6) continue;

    dias.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    );
  }

  return dias;
}

function redondear(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Los totales de un integrante, calculados de lo mismo que ve el detalle.
 *
 * **Ningún total se escribe a mano.** La tabla del equipo, sus tarjetas y la
 * grilla del modal salen todos de acá, así que no pueden decir cosas
 * distintas: era justamente lo que pasaba —el modal mostraba un desvío que no
 * se correspondía con las horas de la fila de la que había salido—.
 */
function sembrar(i: IntegranteDemo): IntegranteDemo {
  const dias = imputacionesDemo(i);
  const suma = (campo: string) =>
    redondear(dias.reduce((s, d) => s + ((d[campo] as number) || 0), 0));

  const planificadas = (i.tramos ?? []).reduce((s, tr) => s + tr.horas, 0);
  const tasa = i.tasa ? Number(i.tasa) : null;
  const conTasa = tasa !== null && tasa > 0 && !!i.tasa_moneda;
  const valuable = conTasa && !i.sin_tasa_en_imputaciones;

  return {
    ...i,
    horas_planificadas: planificadas,
    costo_planificado: conTasa ? Math.round(planificadas * (tasa as number)) : null,

    horas_imputadas_aprobadas: suma('aprobadas'),
    horas_imputadas_sin_aprobar: suma('sin_aprobar'),
    horas_imputadas_rechazadas: suma('rechazadas'),
    horas_fuera_de_plan: suma('fuera_de_plan'),

    /* Lo imputado mes a mes, agrupado de los mismos días: es la contraparte
       de los tramos, que ya viajan abiertos por mes. */
    imputadas_por_mes: porMes(dias),
    horas_fuera_de_plan_aprobadas: suma('fuera_de_plan_aprobadas'),
    horas_fuera_de_plan_sin_aprobar: suma('fuera_de_plan_sin_aprobar'),

    costo_imputado_aprobado: valuable ? suma('costo_aprobado') : null,
    costo_imputado_sin_aprobar: valuable ? suma('costo_sin_aprobar') : null,
    costo_imputado_moneda: valuable ? i.tasa_moneda ?? null : null,
    costo_fuera_de_plan: valuable ? suma('costo_fuera_de_plan') : null,
    costo_fuera_de_plan_aprobado: valuable ? suma('costo_fuera_de_plan_aprobado') : null,
    costo_fuera_de_plan_sin_aprobar: valuable ? suma('costo_fuera_de_plan_sin_aprobar') : null,
    costo_fuera_de_plan_moneda: valuable ? i.tasa_moneda ?? null : null,

    horas_sin_costo: suma('horas_sin_costo')
  };
}

/* Los literales de arriba fijan el tipo de cada campo —un `costo_planificado`
   escrito con número queda como `number` y no admite el `null` que sí puede
   devolver la siembra—, así que el tipo del demo afloja los numéricos. Es dato
   de prueba: lo que importa es que coincida con lo que responde el backend. */
type IntegranteDemo = Omit<
  (typeof INTEGRANTES)[number],
  'costo_planificado' | 'costo_imputado_aprobado' | 'costo_imputado_sin_aprobar'
  | 'costo_imputado_moneda' | 'costo_fuera_de_plan' | 'costo_fuera_de_plan_moneda'
  | 'tasa' | 'tasa_moneda' | 'tramos'
> & {
  tasa: string | null;
  tasa_moneda: string | null;
  tramos: Array<{ desde: string; hasta: string; horas: number }>;
  costo_planificado: number | null;
  costo_imputado_aprobado: number | null;
  costo_imputado_sin_aprobar: number | null;
  costo_imputado_moneda: string | null;
  costo_fuera_de_plan: number | null;
  costo_fuera_de_plan_moneda: string | null;
  sin_tasa_en_imputaciones?: boolean;
  [clave: string]: unknown;
};


/**
 * La curva del proyecto, sumada de las mismas filas que la tabla.
 *
 * Estaba escrita a mano —tres meses con 160, 160 y 120 horas— mientras la
 * tabla, ya sembrada del plan, decía 146 en total: la pantalla se contradecía
 * consigo misma un panel más abajo. Se arma de los tramos y de las
 * imputaciones que de ellos se derivan, que es lo que hace el backend.
 */
function serieMensualDemo(): Array<Record<string, unknown>> {
  const meses = new Map<string, {
    horas_planificadas: number;
    horas_imputadas: number;
    horas_aprobadas: number;
    horas_sin_aprobar: number;
    horas_fuera_de_plan: number;
    costo_planificado: number;
    costo_imputado: number | null;
    costo_aprobado: number | null;
    costo_sin_aprobar: number | null;
    costo_fuera_de_plan: number | null;
  }>();

  const en = (mes: string) => {
    const m = meses.get(mes) ?? {
      horas_planificadas: 0,
      horas_imputadas: 0,
      horas_aprobadas: 0,
      horas_sin_aprobar: 0,
      horas_fuera_de_plan: 0,
      costo_planificado: 0,
      costo_imputado: null,
      costo_aprobado: null,
      costo_sin_aprobar: null,
      costo_fuera_de_plan: null
    };

    meses.set(mes, m);

    return m;
  };

  for (const i of equipo) {
    const tasa = i.tasa ? Number(i.tasa) : null;
    const valuable = tasa !== null && tasa > 0 && !!i.tasa_moneda && !i.sin_tasa_en_imputaciones;

    for (const tr of i.tramos ?? []) {
      const m = en(tr.desde.slice(0, 7));

      m.horas_planificadas = redondear(m.horas_planificadas + tr.horas);

      if (tasa !== null && i.tasa_moneda) {
        m.costo_planificado = Math.round(m.costo_planificado + tr.horas * tasa);
      }
    }

    for (const d of imputacionesDemo(i)) {
      const m = en((d['fecha'] as string).slice(0, 7));
      const aprobadas = (d['aprobadas'] as number) || 0;
      const sinAprobar = (d['sin_aprobar'] as number) || 0;
      const fueraDePlan = (d['fuera_de_plan'] as number) || 0;
      const horas = aprobadas + sinAprobar;

      m.horas_imputadas = redondear(m.horas_imputadas + horas);
      m.horas_aprobadas = redondear(m.horas_aprobadas + aprobadas);
      m.horas_sin_aprobar = redondear(m.horas_sin_aprobar + sinAprobar);
      /* Marca sobre las mismas horas: no se suma con las dos de arriba. */
      m.horas_fuera_de_plan = redondear(m.horas_fuera_de_plan + fueraDePlan);

      /* **Se deja en nulo si alguna hora del mes no se puede valuar.** Un
         costo parcial rotulado como el del mes se lee como el total. */
      if (valuable) {
        const t = tasa as number;

        m.costo_imputado = Math.round((m.costo_imputado ?? 0) + horas * t);
        m.costo_aprobado = Math.round((m.costo_aprobado ?? 0) + aprobadas * t);
        m.costo_sin_aprobar = Math.round((m.costo_sin_aprobar ?? 0) + sinAprobar * t);
        m.costo_fuera_de_plan = Math.round((m.costo_fuera_de_plan ?? 0) + fueraDePlan * t);
      }
    }
  }

  return [...meses.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([mes, m]) => ({ mes, ...m }));
}


/* Una cotización de mentira, para poder probar el selector en modo vista
   previa. La de verdad la da ARMUS; acá alcanza con que sea una sola y esté
   marcada como inventada. */
const TC_DEMO: Record<string, number> = { ARS: 1, USD: 1000 };

/**
 * El integrante visto en otra moneda.
 *
 * Sin esto, el modo vista previa devolvía siempre los mismos pesos y el
 * selector de la cabecera parecía roto: se elegían dólares y la pantalla
 * seguía en pesos, que es exactamente el síntoma de que no hay cotización.
 *
 * **Se convierte todo o nada.** Si falta la cotización de alguna de las dos
 * monedas, se devuelven los importes en la suya —con su moneda, que viaja
 * aparte—: nunca el número bajo otro rótulo.
 */
function enLaMoneda(i: IntegranteDemo, moneda: string | null): IntegranteDemo {
  if (!moneda) return i;

  const suya = i.tasa_moneda ?? null;
  const factor = suya && TC_DEMO[suya] && TC_DEMO[moneda]
    ? TC_DEMO[suya] / TC_DEMO[moneda]
    : null;

  if (factor === null) {
    return { ...i, tasa_vista: null, moneda_vista: moneda };
  }

  const conv = (v: unknown) =>
    typeof v === 'number' ? Math.round(v * factor * 100) / 100 : null;

  return {
    ...i,
    tasa_vista: i.tasa ? Math.round(Number(i.tasa) * factor * 100) / 100 : null,
    moneda_vista: moneda,
    costo_planificado: conv(i.costo_planificado),
    costo_imputado_aprobado: conv(i.costo_imputado_aprobado),
    costo_imputado_sin_aprobar: conv(i.costo_imputado_sin_aprobar),
    costo_imputado_moneda: i.costo_imputado_moneda ? moneda : null,
    costo_fuera_de_plan: conv(i.costo_fuera_de_plan),
    costo_fuera_de_plan_aprobado: conv(i['costo_fuera_de_plan_aprobado']),
    costo_fuera_de_plan_sin_aprobar: conv(i['costo_fuera_de_plan_sin_aprobar']),
    costo_fuera_de_plan_moneda: i.costo_fuera_de_plan_moneda ? moneda : null
  };
}


/** Agrupa por mes los días imputados, como hace el backend */
function porMes(dias: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  const meses = new Map<string, { mes: string; aprobadas: number; sin_aprobar: number; fuera_de_plan: number }>();

  for (const d of dias) {
    const mes = (d['fecha'] as string).slice(0, 7);
    const m = meses.get(mes) ?? { mes, aprobadas: 0, sin_aprobar: 0, fuera_de_plan: 0 };

    m.aprobadas = redondear(m.aprobadas + ((d['aprobadas'] as number) || 0));
    m.sin_aprobar = redondear(m.sin_aprobar + ((d['sin_aprobar'] as number) || 0));
    m.fuera_de_plan = redondear(m.fuera_de_plan + ((d['fuera_de_plan'] as number) || 0));

    meses.set(mes, m);
  }

  return [...meses.values()].sort((a, b) => a.mes.localeCompare(b.mes));
}
